import { useState, useEffect } from 'preact/hooks';
import { api } from '../../api/client';
import { isAdmin } from '../../stores/auth.store';
import { scStyles } from './styles';
import { SkeletonBar } from '../ui';
import { T } from '../../styles/tokens';
import { Button } from '../ui';

export function TabSettings() {
  const [manifest, setManifest] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [actionLoading, setActionLoading] = useState('');
  const [toast, setToast] = useState('');
  const [stats, setStats] = useState<any>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [m, s] = await Promise.all([
        api.get<any>('/cloud/skills-pack/manifest'),
        api.get<any>('/cloud/all-skills').catch(() => null),
      ]);
      const mData = (m as any)?.data || m;
      setManifest(mData);
      const sList = (s as any)?.skills || (s as any)?.data?.skills || (s as any)?.data || [];
      setStats({
        total: Array.isArray(sList) ? sList.length : 0,
        tier1: (mData?.skills || []).filter((sk: any) => sk.tier === 'tier1').length,
        tier2: (mData?.skills || []).filter((sk: any) => sk.tier === 'tier2').length,
      });

      // Check updates
      const ver = mData?.version;
      if (ver) {
        api.get<any>(`/cloud/skills-pack/check-updates?currentVersion=${ver}`)
          .then((r: any) => { if ((r as any)?.hasUpdate || (r as any)?.data?.hasUpdate) setUpdateAvailable(true); })
          .catch(() => {});
      }
    } catch {}
    setLoading(false);
  };

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const handleCheckUpdates = async () => {
    setActionLoading('check');
    try {
      const r = await api.get<any>(`/cloud/skills-pack/check-updates?currentVersion=${manifest?.version || '0'}`);
      const has = (r as any)?.hasUpdate || (r as any)?.data?.hasUpdate;
      setUpdateAvailable(!!has);
      showToast(has ? 'Nova versão disponível!' : 'Já está atualizado');
    } catch { showToast('Erro ao verificar'); }
    setActionLoading('');
  };

  const handleUpdate = async () => {
    setActionLoading('update');
    try {
      await api.post('/cloud/admin/skills-pack/update', {});
      showToast('Versão atualizada!');
      loadData();
    } catch { showToast('Erro ao atualizar'); }
    setActionLoading('');
  };

  const handlePushUpdate = async () => {
    setActionLoading('push');
    try {
      await api.post('/cloud/admin/skills-pack/push-update', {});
      showToast('Push enviado para todas as máquinas!');
    } catch { showToast('Erro ao enviar push'); }
    setActionLoading('');
  };

  if (!isAdmin.value) return (
    <div style={{ textAlign: 'center', padding: '60px 20px', color: T.danger }}>
 <div style={{ fontSize: '48px', marginBottom: '12px' }}></div>
      <div style={{ fontSize: '16px', fontWeight: 600 }}>Acesso restrito a administradores</div>
    </div>
  );

  if (loading) return <div style={{ padding: 16 }}><SkeletonBar width="50%" height={14} style={{ marginBottom: 8 }} /><SkeletonBar width="30%" height={14} /></div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px' }}>
        {[
          { label: 'Total Skills', value: stats?.total || 0, color: T.purple },
          { label: 'Tier 1 (Verificadas)', value: stats?.tier1 || 0, color: T.success },
          { label: 'Tier 2 (Recomendadas)', value: stats?.tier2 || 0, color: T.warning },
          { label: 'Versão Atual', value: manifest?.version || '?', color: T.primary },
        ].map((s, i) => (
          <div key={i} style={{ ...scStyles.glass, padding: '16px 20px', textAlign: 'center' }}>
            <div style={{ fontSize: '28px', fontWeight: 800, color: s.color, animation: 'countUp 0.5s ease' }}>{s.value}</div>
            <div style={{ fontSize: '12px', color: T.textSecondary, marginTop: '4px' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Skills Pack Info */}
      <div style={{ ...scStyles.glass, padding: '20px' }}>
        <h3 style={{ fontSize: '16px', fontWeight: 700, color: T.text, marginBottom: '16px' }}>Skills Pack</h3>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <Button variant="ghost" onClick={handleCheckUpdates} disabled={actionLoading === 'check'}
            style={{ padding: '10px 20px', borderRadius: '10px', border: `1px solid ${T.primaryBgStrong}`, background: T.primaryBg, color: T.primary, fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
 {actionLoading === 'check' ? 'Verificando...' : 'Verificar Atualizações'}
          </Button>
          {updateAvailable && (
            <Button variant="ghost" onClick={handleUpdate} disabled={actionLoading === 'update'}
              style={{ padding: '10px 20px', borderRadius: '10px', border: 'none', background: 'linear-gradient(135deg, #10B981, #059669)', color: T.text, fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
 {actionLoading === 'update' ? 'Atualizando...' : 'Atualizar Versão'}
            </Button>
          )}
          <Button variant="ghost" onClick={handlePushUpdate} disabled={actionLoading === 'push'}
            style={{ padding: '10px 20px', borderRadius: '10px', border: 'none', background: 'linear-gradient(135deg, #7c3aed, #a855f7)', color: T.text, fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
 {actionLoading === 'push' ? 'Enviando...' : 'Push Update'}
          </Button>
        </div>
      </div>

      {/* Skills List (editable) */}
      <div style={{ ...scStyles.glass, padding: '20px' }}>
        <h3 style={{ fontSize: '16px', fontWeight: 700, color: T.text, marginBottom: '16px' }}>Skills no Pack ({(manifest?.skills || []).length})</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '400px', overflow: 'auto' }}>
          {(manifest?.skills || []).map((s: any, i: number) => (
            <div key={s.name || i} style={{
              display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px',
              borderRadius: '8px', background: T.purpleBg,
            }}>
 <span style={{ fontSize: '18px' }}>{s.emoji || ''}</span>
              <span style={{ flex: 1, fontSize: '13px', color: T.text, fontWeight: 500 }}>{s.name}</span>
              <span style={{ fontSize: '11px', color: T.textSecondary }}>{s.tier || 'optional'}</span>
 {s.verified ? <span style={{ fontSize: '11px', color: T.success }}></span> : null}
            </div>
          ))}
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: '24px', right: '24px', padding: '12px 20px',
          borderRadius: '10px', background: T.bgOverlay, border: `1px solid ${T.purpleBgStrong}`,
          color: T.text, fontSize: '13px', fontWeight: 600, zIndex: 3000, animation: 'fadeInUp 0.3s ease',
        }}>
          {toast}
        </div>
      )}
    </div>
  );
}
